import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Assessment.css';

// ── Audio Engine ─────────────────────────────────────────────────
const AudioEngine = (() => {
  let ctx = null;
  const get = () => {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    return ctx;
  };

  const tone = (freq, type = 'sine', dur = 0.3, vol = 0.3, delay = 0) => {
    try {
      const c = get();
      const o = c.createOscillator();
      const g = c.createGain();
      o.connect(g); g.connect(c.destination);
      o.frequency.value = freq; o.type = type;
      const t = c.currentTime + delay;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(vol, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      o.start(t); o.stop(t + dur);
    } catch (e) {}
  };

  return {
    click: () => tone(880, 'sine', 0.08, 0.15),
    hover: () => tone(1200, 'sine', 0.05, 0.07),
    correct: () => {
      [523, 659, 784, 1047].forEach((f, i) => tone(f, 'sine', 0.25, 0.2, i * 0.08));
    },
    wrong: () => {
      [330, 277, 220].forEach((f, i) => tone(f, 'sawtooth', 0.25, 0.18, i * 0.1));
    },
    coin: () => {
      [1047, 1319, 1568, 2093].forEach((f, i) => tone(f, 'sine', 0.15, 0.25, i * 0.06));
    },
    achievement: () => {
      [523, 659, 784, 1047, 1319].forEach((f, i) => tone(f, 'sine', 0.3, 0.22, i * 0.07));
      setTimeout(() => tone(1568, 'sine', 0.5, 0.3), 400);
    },
    levelUp: () => {
      [392, 523, 659, 784, 1047].forEach((f, i) => tone(f, 'triangle', 0.35, 0.25, i * 0.09));
    },
    submit: () => {
      [261, 329, 392, 523, 659, 784, 1047].forEach((f, i) => tone(f, 'sine', 0.4, 0.22, i * 0.07));
    },
    navigate: () => tone(660, 'sine', 0.1, 0.12),
    // Professional subtle tones for 6-12
    proClick: () => tone(660, 'sine', 0.06, 0.08),
    proCorrect: () => { [523, 659, 784].forEach((f, i) => tone(f, 'sine', 0.2, 0.12, i * 0.07)); },
    proWrong: () => tone(280, 'sine', 0.18, 0.1),
    proSubmit: () => { [392, 523, 659, 784].forEach((f, i) => tone(f, 'sine', 0.25, 0.12, i * 0.08)); },
  };
})();

// ── Constants ────────────────────────────────────────────────────
const getDifficultyIcon = (d) => ({ easy: '✦', medium: '◈', hard: '⬡' }[d] || '◈');
const getDifficultyColor = (d) => ({ easy: 'diff-easy', medium: 'diff-medium', hard: 'diff-hard' }[d] || 'diff-medium');

const BADGE_CONFIG = {
  SUPER_HARD_ATTEMPT: { icon: '🦁', title: 'Fearless', color: '#ff4757', description: 'Attempted a hard question!' },
  SPEED_DEMON:        { icon: '⚡', title: 'Speed Demon', color: '#ffa502', description: 'Answered in under 12 seconds!' },
  PERFECT_STREAK_3:   { icon: '🔥', title: 'Hot Streak', color: '#ff6b35', description: '3 correct in a row!' },
  PERFECT_STREAK_5:   { icon: '🌟', title: 'Star Power', color: '#eccc68', description: '5 correct in a row!' },
  PERFECT_STREAK_10:  { icon: '🚀', title: 'Unstoppable', color: '#7bed9f', description: '10 correct in a row!' },
  FIRST_ATTEMPT:      { icon: '👣', title: 'First Steps', color: '#a29bfe', description: 'Started your journey!' },
  PERFECT_SCORE:      { icon: '💯', title: 'Perfect', color: '#55efc4', description: 'Perfect score!' },
  HIGH_SCORER:        { icon: '🎯', title: 'High Scorer', color: '#74b9ff', description: 'High score achieved!' },
  QUICK_THINKER:      { icon: '🧠', title: 'Quick Mind', color: '#fd79a8', description: 'Blazing fast answers!' },
  FLAWLESS_VICTORY:   { icon: '🏆', title: 'Flawless', color: '#fdcb6e', description: 'All answers correct!' },
  DETERMINED_LEARNER: { icon: '💪', title: 'Determined', color: '#ff6b6b', description: 'Completed all questions!' },
  SOLID_PERFORMER:    { icon: '📚', title: 'Solid Performer', color: '#4ecdc4', description: 'Great consistency!' },
};

const ENCOURAGEMENT = {
  primary:   ['Awesome! 🌟', 'Super! ⭐', 'You Rock! 💫', 'Yay! 🎈', 'Amazing! 🚀', 'Brilliant! 🎉', 'Woo Hoo! 🧠', 'Legend! 🎸', 'YES! 💪', 'Magic! ✨'],
  secondary: ['Excellent!', 'Well Done!', 'Nailed It!', 'Keep Going!', 'Nice One!', 'Impressive!', 'Big Brain!', 'Correct!', 'Way to Go!', 'Perfect!'],
};

// ── Particle Canvas (Kids only) ──────────────────────────────────
const ParticleCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      r: Math.random() * 2 + 0.5, dx: (Math.random() - 0.5) * 0.3, dy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.5 + 0.1,
      color: ['#a78bfa', '#60a5fa', '#34d399', '#f472b6'][Math.floor(Math.random() * 4)],
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color; ctx.globalAlpha = p.alpha; ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} className="particle-canvas" />;
};

// ── Floating Coins (Kids only) ───────────────────────────────────
const CoinRain = ({ active, count = 30 }) => {
  const [coins, setCoins] = useState([]);
  useEffect(() => {
    if (!active) return;
    const newCoins = Array.from({ length: count }, (_, i) => ({
      id: Date.now() + i,
      left: Math.random() * 100,
      delay: Math.random() * 1.5,
      size: Math.random() * 20 + 20,
      rot: Math.random() * 360,
      type: Math.random() > 0.5 ? '🪙' : '💰',
    }));
    setCoins(newCoins);
    const t = setTimeout(() => setCoins([]), 3500);
    return () => clearTimeout(t);
  }, [active]);
  return (
    <div className="coin-rain-wrap" aria-hidden>
      {coins.map(c => (
        <div key={c.id} className="coin-fall" style={{ left: `${c.left}%`, animationDelay: `${c.delay}s`, fontSize: `${c.size}px`, '--rot': `${c.rot}deg` }}>
          {c.type}
        </div>
      ))}
    </div>
  );
};

// ── XP Bar (Kids) ────────────────────────────────────────────────
const XPBar = ({ current, total, score }) => {
  const pct = total > 0 ? (current / total) * 100 : 0;
  return (
    <div className="xp-bar-wrap">
      <div className="xp-label">
        <span>Q {current}/{total}</span>
        <span className="xp-score">⚡ {score} XP</span>
      </div>
      <div className="xp-track">
        <div className="xp-fill" style={{ width: `${pct}%` }} />
        <div className="xp-glow" style={{ left: `${pct}%` }} />
      </div>
    </div>
  );
};

// ── Progress Bar (Professional) ──────────────────────────────────
const ProProgressBar = ({ current, total, score }) => {
  const pct = total > 0 ? (current / total) * 100 : 0;
  return (
    <div className="pro-progress-wrap">
      <div className="pro-progress-label">
        <span>{current} of {total} questions</span>
        <span className="pro-score-label">{score} pts</span>
      </div>
      <div className="pro-progress-track">
        <div className="pro-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ── Accuracy Display (Kids) ──────────────────────────────────────
const AccuracyDisplay = ({ correct, total }) => {
  const acc = total > 0 ? Math.round((correct / total) * 100) : 0;
  const col = acc >= 80 ? '#34d399' : acc >= 60 ? '#fbbf24' : '#f87171';
  return (
    <div className="accuracy-display">
      <div className="accuracy-circle" style={{ borderColor: col }}>
        <span className="accuracy-value" style={{ color: col }}>{acc}%</span>
      </div>
      <div className="accuracy-label">Accuracy</div>
    </div>
  );
};

// ── Pro Accuracy (Professional) ──────────────────────────────────
const ProAccuracyDisplay = ({ correct, total }) => {
  const acc = total > 0 ? Math.round((correct / total) * 100) : 0;
  return (
    <div className="pro-accuracy">
      <span className="pro-accuracy-val">{acc}%</span>
      <span className="pro-accuracy-lbl">Accuracy</span>
    </div>
  );
};

// ── Achievement Toast ─────────────────────────────────────────────
const AchievementToast = ({ badge, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 4500);
    return () => clearTimeout(t);
  }, [onDone]);
  const cfg = BADGE_CONFIG[badge] || { icon: '🏆', title: 'Achievement', color: '#a78bfa', description: 'Unlocked!' };
  return (
    <div className="ach-toast" style={{ '--ach-color': cfg.color }}>
      <div className="ach-coin-burst">
        {[...Array(6)].map((_, i) => <span key={i} className="ach-coin" style={{ '--i': i }}>🪙</span>)}
      </div>
      <span className="ach-icon">{cfg.icon}</span>
      <div className="ach-info">
        <div className="ach-title">🏅 Achievement Unlocked!</div>
        <div className="ach-name">{cfg.title}</div>
        <div className="ach-desc">{cfg.description}</div>
      </div>
      <div className="ach-xp-pop">+50 XP</div>
    </div>
  );
};

// ── Pro Notification Toast ────────────────────────────────────────
const ProNotificationToast = ({ badge, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3500);
    return () => clearTimeout(t);
  }, [onDone]);
  const cfg = BADGE_CONFIG[badge] || { icon: '✓', title: 'Milestone', color: '#3b82f6', description: 'Achieved!' };
  return (
    <div className="pro-notif-toast">
      <span className="pro-notif-icon">{cfg.icon}</span>
      <div className="pro-notif-info">
        <div className="pro-notif-name">{cfg.title}</div>
        <div className="pro-notif-desc">{cfg.description}</div>
      </div>
    </div>
  );
};

// ── Streak Badge ─────────────────────────────────────────────────
const StreakBadge = ({ streak }) => {
  if (streak < 2) return null;
  return (
    <div className={`streak-badge ${streak >= 5 ? 'streak-fire' : ''}`}>
      <span>{streak >= 5 ? '🔥' : '⚡'}</span>
      <span>{streak}x</span>
    </div>
  );
};

// ── Encouragement Popup (Kids) ────────────────────────────────────
const EncouragementMessage = ({ message, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 1800); return () => clearTimeout(t); }, [onClose]);
  return <div className="encouragement-pop" aria-live="polite">{message}</div>;
};

// ── Pro Correct Flash (Professional) ─────────────────────────────
const ProCorrectFlash = ({ onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 1200); return () => clearTimeout(t); }, [onClose]);
  return <div className="pro-correct-flash" aria-live="polite">Correct</div>;
};

// ── Difficulty Change Notif ───────────────────────────────────────
const DifficultyChangeNotif = ({ oldDiff, newDiff, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 2800); return () => clearTimeout(t); }, [onClose]);
  const up = newDiff === 'hard' || (newDiff === 'medium' && oldDiff === 'easy');
  return (
    <div className={`diff-notif ${up ? 'diff-notif-up' : 'diff-notif-down'}`}>
      <span>{up ? '⬆️' : '⬇️'}</span>
      <span>Difficulty: <b>{newDiff}</b></span>
    </div>
  );
};

// ── Pro Difficulty Change ─────────────────────────────────────────
const ProDifficultyChangeNotif = ({ oldDiff, newDiff, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 2500); return () => clearTimeout(t); }, [onClose]);
  const up = newDiff === 'hard' || (newDiff === 'medium' && oldDiff === 'easy');
  return (
    <div className={`pro-diff-notif ${up ? 'pro-diff-up' : 'pro-diff-down'}`}>
      <span className="pro-diff-arrow">{up ? '↑' : '↓'}</span>
      <span>Adaptive difficulty adjusted to <strong>{newDiff}</strong></span>
    </div>
  );
};

// ── Gamified First-Time Tour (Kids) ──────────────────────────────
const GameTour = ({ onComplete, isPrimary }) => {
  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(false);

  const steps = [
    { icon: '🎮', title: 'Welcome, Player!', subtitle: 'SolveSmart: The Learning Game', desc: 'Your mission? Answer questions, earn XP, unlock badges, and climb the leaderboard. Are you ready?', color: '#a78bfa', features: ['🧠 AI adapts to your level', '⚡ Earn XP for every answer', '🏆 Unlock secret badges', '🔥 Build epic streaks'] },
    { icon: '🤖', title: 'Adaptive AI Boss', subtitle: 'The engine watches your every move', desc: 'Answer correctly → harder questions. Struggle → easier ones. It\'s always the right challenge!', color: '#60a5fa', features: ['Easy → Medium → Hard difficulty', 'Focus on your weak spots', 'Personalized just for YOU', 'No two players are the same'] },
    { icon: '⚡', title: 'Earn XP & Level Up', subtitle: 'Every answer counts', desc: 'Correct answers = XP. Harder questions = MORE XP. Build streaks for bonus rewards!', color: '#fbbf24', features: ['Easy: +10 XP', 'Medium: +20 XP', 'Hard: +30 XP', '🔥 Streaks multiply your score'] },
    { icon: '🏅', title: 'Unlock Achievements', subtitle: 'Hidden badges await', desc: 'Speed answers, perfect streaks, flawless victories — over 12 secret badges to collect!', color: '#34d399', features: ['👣 First Steps — start your journey', '🔥 Hot Streak — 3 in a row', '⚡ Speed Demon — answer fast', '🏆 Flawless — perfect score'] },
    { icon: '🎯', title: 'READY, PLAYER ONE?', subtitle: 'Your adventure begins NOW', desc: 'Press START to begin. Good luck — you\'ll need it on HARD mode! 😈', color: '#f472b6', features: ['Press 1/2/3/4 for keyboard shortcuts', 'Watch the accuracy tracker', 'Chase the leaderboard top spot', 'Collect all 12 badges!'] },
  ];

  const current = steps[step];
  const next = () => {
    AudioEngine.navigate();
    if (step < steps.length - 1) { setAnimating(true); setTimeout(() => { setStep(s => s + 1); setAnimating(false); }, 200); }
    else { AudioEngine.levelUp(); onComplete(); }
  };
  const skip = () => { AudioEngine.click(); onComplete(); };

  return (
    <div className="tour-overlay">
      <div className="tour-modal" style={{ '--tour-color': current.color }}>
        <div className="tour-stars">{[...Array(20)].map((_, i) => <div key={i} className="tour-star" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 2}s` }} />)}</div>
        <div className={`tour-content ${animating ? 'tour-exit' : 'tour-enter'}`}>
          <div className="tour-dots">
            {steps.map((_, i) => (
              <div key={i} className={`tour-dot ${i === step ? 'active' : i < step ? 'done' : ''}`} onClick={() => { AudioEngine.click(); setStep(i); }} />
            ))}
          </div>
          <div className="tour-icon-wrap">
            <div className="tour-icon-ring" />
            <span className="tour-icon">{current.icon}</span>
          </div>
          <div className="tour-badge-label" style={{ color: current.color }}>STAGE {step + 1}/{steps.length}</div>
          <h2 className="tour-title">{current.title}</h2>
          <p className="tour-subtitle">{current.subtitle}</p>
          <p className="tour-desc">{current.desc}</p>
          <div className="tour-features">
            {current.features.map((f, i) => (
              <div key={i} className="tour-feature" style={{ animationDelay: `${i * 0.08}s` }}>
                <span className="tour-feature-check">✓</span>
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="tour-footer">
          <button className="tour-skip" onClick={skip}>Skip Tour</button>
          <button className="tour-next" style={{ background: current.color }} onClick={next} onMouseEnter={() => AudioEngine.hover()}>
            {step === steps.length - 1 ? '🎮 START GAME!' : 'Next Stage →'}
          </button>
        </div>
        <div className="tour-progress-bar">
          <div className="tour-progress-fill" style={{ width: `${((step + 1) / steps.length) * 100}%`, background: current.color }} />
        </div>
      </div>
    </div>
  );
};

// ── Pro Onboarding (Grades 6-12) ──────────────────────────────────
const ProOnboarding = ({ onComplete }) => {
  const [step, setStep] = useState(0);

  const steps = [
    { title: 'Adaptive Assessment', desc: 'This assessment adapts in real-time based on your responses. Questions will adjust in difficulty to accurately measure your understanding.', icon: '◎', items: ['AI-powered difficulty calibration', 'Personalized question selection', 'Comprehensive topic coverage'] },
    { title: 'Scoring System', desc: 'Points are awarded based on correctness and question difficulty. Your accuracy and consistency matter most.', icon: '◈', items: ['Easy: 10 pts · Medium: 20 pts · Hard: 30 pts', 'Streaks award milestone recognition', 'Final grade reflects overall performance'] },
    { title: "You're Ready", desc: 'Use keyboard shortcuts 1–4 to select answers. Take your time — accuracy over speed.', icon: '→', items: ['Press 1/2/3/4 for quick selection', 'Review the difficulty indicator on each card', 'Your answers are submitted automatically'] },
  ];

  const current = steps[step];

  return (
    <div className="pro-onboard-overlay">
      <div className="pro-onboard-modal">
        <div className="pro-onboard-step-row">
          {steps.map((_, i) => <div key={i} className={`pro-onboard-step-dot ${i === step ? 'active' : i < step ? 'done' : ''}`} />)}
        </div>
        <div className="pro-onboard-icon">{current.icon}</div>
        <h2 className="pro-onboard-title">{current.title}</h2>
        <p className="pro-onboard-desc">{current.desc}</p>
        <ul className="pro-onboard-items">
          {current.items.map((item, i) => <li key={i}><span>—</span><span>{item}</span></li>)}
        </ul>
        <div className="pro-onboard-footer">
          <span className="pro-onboard-count">{step + 1} / {steps.length}</span>
          <div className="pro-onboard-actions">
            {step > 0 && <button className="pro-onboard-back" onClick={() => setStep(s => s - 1)}>Back</button>}
            <button className="pro-onboard-next" onClick={() => { if (step < steps.length - 1) setStep(s => s + 1); else onComplete(); }}>
              {step === steps.length - 1 ? 'Begin Assessment' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Submit Celebration (Kids) ─────────────────────────────────────
const SubmitCelebration = ({ correct, total, xp, badges, accuracy }) => {
  const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 100); AudioEngine.submit(); }, []);
  const grade = accuracy >= 90 ? 'S' : accuracy >= 80 ? 'A' : accuracy >= 70 ? 'B' : accuracy >= 60 ? 'C' : 'D';
  const gradeColor = { S: '#ffd700', A: '#34d399', B: '#60a5fa', C: '#fbbf24', D: '#f87171' }[grade];
  return (
    <div className={`submit-overlay ${show ? 'visible' : ''}`}>
      <CoinRain active count={50} />
      <div className="submit-modal">
        <div className="submit-grade-ring" style={{ '--grade-color': gradeColor }}>
          <div className="submit-grade" style={{ color: gradeColor }}>{grade}</div>
        </div>
        <h2 className="submit-title">ASSESSMENT COMPLETE!</h2>
        <p className="submit-sub">Your final results are in...</p>
        <div className="submit-stats">
          {[
            { label: 'Correct', val: correct, icon: '✅' },
            { label: 'Total', val: total, icon: '📋' },
            { label: 'Accuracy', val: `${accuracy}%`, icon: '🎯' },
            { label: 'XP Earned', val: xp, icon: '⚡' },
            { label: 'Badges', val: badges, icon: '🏅' },
          ].map((s, i) => (
            <div key={i} className="submit-stat" style={{ animationDelay: `${i * 0.1 + 0.3}s` }}>
              <span className="submit-stat-icon">{s.icon}</span>
              <span className="submit-stat-val">{s.val}</span>
              <span className="submit-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
        <div className="submit-progress-track">
          <div className="submit-progress-fill" style={{ width: `${accuracy}%`, background: gradeColor }} />
        </div>
        <div className="submit-redirect">Redirecting to dashboard...</div>
      </div>
    </div>
  );
};

// ── Pro Submit Modal (Grades 6-12) ────────────────────────────────
const ProSubmitModal = ({ correct, total, xp, badges, accuracy }) => {
  const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 100); AudioEngine.proSubmit(); }, []);

  const grade = accuracy >= 90 ? 'A+' : accuracy >= 80 ? 'A' : accuracy >= 70 ? 'B' : accuracy >= 60 ? 'C' : 'D';
  const gradeLabel = accuracy >= 90 ? 'Outstanding' : accuracy >= 80 ? 'Excellent' : accuracy >= 70 ? 'Good' : accuracy >= 60 ? 'Satisfactory' : 'Needs Improvement';

  return (
    <div className={`pro-submit-overlay ${show ? 'visible' : ''}`}>
      <div className="pro-submit-modal">
        <div className="pro-submit-header">
          <div className="pro-submit-grade-block">
            <span className="pro-submit-grade">{grade}</span>
            <span className="pro-submit-grade-label">{gradeLabel}</span>
          </div>
          <div className="pro-submit-divider" />
          <div className="pro-submit-info">
            <h2 className="pro-submit-title">Assessment Complete</h2>
            <p className="pro-submit-sub">Your performance has been recorded</p>
          </div>
        </div>
        <div className="pro-submit-metrics">
          {[
            { label: 'Correct Answers', val: `${correct}/${total}`, sub: `${accuracy}% accuracy` },
            { label: 'Points Earned', val: xp, sub: 'Total score' },
            { label: 'Milestones', val: badges, sub: 'Achieved' },
          ].map((m, i) => (
            <div key={i} className="pro-submit-metric" style={{ animationDelay: `${i * 0.1 + 0.2}s` }}>
              <span className="pro-submit-metric-val">{m.val}</span>
              <span className="pro-submit-metric-label">{m.label}</span>
              <span className="pro-submit-metric-sub">{m.sub}</span>
            </div>
          ))}
        </div>
        <div className="pro-submit-bar-track">
          <div className="pro-submit-bar-fill" style={{ width: `${accuracy}%` }} />
        </div>
        <p className="pro-submit-redirect">Redirecting to your dashboard...</p>
      </div>
    </div>
  );
};

// ── Distribution Modal ────────────────────────────────────────────
const DistributionModal = ({ data, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="dist-modal" onClick={e => e.stopPropagation()}>
      <div className="dist-header">
        <span>📊 Question Distribution</span>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>
      <div className="dist-body">
        {data ? Object.entries(data).map(([name, info]) => (
          <div key={name} className="dist-row">
            <div className="dist-row-info">
              <span className="dist-name">{name}</span>
              <span className={`dist-priority dist-priority-${info.priority || 'medium'}`}>
                {info.priority === 'new' ? '🆕' : info.priority === 'high' ? '🔴' : info.priority === 'medium' ? '🟡' : '🟢'}
              </span>
            </div>
            <span className="dist-count">{info.quota} Q</span>
          </div>
        )) : <p className="dist-empty">No data available.</p>}
      </div>
    </div>
  </div>
);

// ── Pro Distribution Modal ────────────────────────────────────────
const ProDistributionModal = ({ data, onClose }) => (
  <div className="pro-modal-overlay" onClick={onClose}>
    <div className="pro-dist-modal" onClick={e => e.stopPropagation()}>
      <div className="pro-dist-header">
        <span className="pro-dist-title">Topic Distribution</span>
        <button className="pro-close-btn" onClick={onClose}>×</button>
      </div>
      <div className="pro-dist-body">
        {data ? Object.entries(data).map(([name, info]) => (
          <div key={name} className="pro-dist-row">
            <span className="pro-dist-name">{name}</span>
            <span className="pro-dist-count">{info.quota} questions</span>
          </div>
        )) : <p className="pro-dist-empty">No distribution data available.</p>}
      </div>
    </div>
  </div>
);

// ── Main Assessment Component ─────────────────────────────────────
const Assessment = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Calibrating AI engine...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [nextQuestionLoading, setNextQuestionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timer, setTimer] = useState('00:00');
  const [userData, setUserData] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(15);
  const [xp, setXp] = useState(0);
  const [toastQueue, setToastQueue] = useState([]);
  const [answerFeedback, setAnswerFeedback] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [distributionData, setDistributionData] = useState(null);
  const [showDifficultyChange, setShowDifficultyChange] = useState(null);
  const [encouragementMessage, setEncouragementMessage] = useState(null);
  const [showCoinRain, setShowCoinRain] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [isPrimaryGrade, setIsPrimaryGrade] = useState(false);
  const [accuracy, setAccuracy] = useState(0);
  const [xpPopups, setXpPopups] = useState([]);
  const awardedBadgesRef = useRef(new Set());

  const [realTimeAdaptive, setRealTimeAdaptive] = useState({
    currentDifficulty: 'medium',
    performance: { correct: 0, total: 0, consecutiveCorrect: 0, consecutiveWrong: 0 },
    questionsGenerated: 0,
    totalQuestions: 15,
    selected_subconcepts: [],
  });

  const [accurateScoreTracker, setAccurateScoreTracker] = useState({
    userAnswers: {}, correctCount: 0, totalQuestions: 0, conceptPerformance: {},
  });

  const [earnedBadges, setEarnedBadges] = useState([]);
  const [currentAchievements, setCurrentAchievements] = useState({
    perfectStreak: 0, superHardAttempted: false, speedDemon: false, firstAttempt: true,
  });

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const qStartTimeRef = useRef(null);
  const qTimesRef = useRef({});

  useEffect(() => {
    const total = accurateScoreTracker.totalQuestions || realTimeAdaptive.performance.total;
    const correct = accurateScoreTracker.correctCount;
    setAccuracy(total > 0 ? Math.round((correct / total) * 100) : 0);
  }, [accurateScoreTracker.correctCount, accurateScoreTracker.totalQuestions, realTimeAdaptive.performance.total]);

  useEffect(() => {
    if (userData?.grade) setIsPrimaryGrade(userData.grade <= 5);
  }, [userData]);

  useEffect(() => {
    const visited = localStorage.getItem('solvesmart_visited');
    if (!visited && assessmentStarted) setTimeout(() => setShowGuide(true), 600);
  }, [assessmentStarted]);

  const addToast = useCallback((key) => {
    setToastQueue(p => [...p, { id: Date.now() + Math.random(), badge: key }]);
  }, []);

  const awardBadge = useCallback((key) => {
    if (awardedBadgesRef.current.has(key)) return;
    awardedBadgesRef.current.add(key);
    setEarnedBadges(prev => [...prev, key]);
    addToast(key);
  }, [addToast]);

  const showEncouragement = useCallback(() => {
    const msgs = isPrimaryGrade ? ENCOURAGEMENT.primary : ENCOURAGEMENT.secondary;
    setEncouragementMessage(msgs[Math.floor(Math.random() * msgs.length)]);
  }, [isPrimaryGrade]);

  const checkAchievements = useCallback((qIdx, isCorrect, timeSpent, difficulty) => {
    if (difficulty === 'hard' && !currentAchievements.superHardAttempted) {
      setCurrentAchievements(p => ({ ...p, superHardAttempted: true }));
      awardBadge('SUPER_HARD_ATTEMPT');
    }
    if (timeSpent < 12 && isCorrect && !currentAchievements.speedDemon) {
      setCurrentAchievements(p => ({ ...p, speedDemon: true }));
      awardBadge('SPEED_DEMON');
    }
    if (isCorrect) {
      const streak = currentAchievements.perfectStreak + 1;
      setCurrentAchievements(p => ({ ...p, perfectStreak: streak }));
      if (streak === 3) awardBadge('PERFECT_STREAK_3');
      else if (streak === 5) awardBadge('PERFECT_STREAK_5');
      else if (streak === 10) awardBadge('PERFECT_STREAK_10');
    } else {
      setCurrentAchievements(p => ({ ...p, perfectStreak: 0 }));
    }
    if (qIdx === 0 && currentAchievements.firstAttempt) {
      setCurrentAchievements(p => ({ ...p, firstAttempt: false }));
      awardBadge('FIRST_ATTEMPT');
    }
  }, [currentAchievements, awardBadge]);

  const startAdaptive = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/start-real-time-adaptive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const data = await res.json();
      if (data.success) {
        setRealTimeAdaptive(p => ({ ...p, totalQuestions: data.total_questions, selected_subconcepts: data.selected_subconcepts || [] }));
        setAccurateScoreTracker(p => ({ ...p, totalQuestions: data.total_questions }));
        setTotalQuestions(data.total_questions);
        if (data.distribution_summary) setDistributionData(data.distribution_summary.distribution);
        setLoadingProgress(40);
        await generateNextQuestion();
      } else throw new Error(data.message || 'Start failed');
    } catch (err) { setError(err.message); setLoading(false); }
  }, []);

  const generateNextQuestion = useCallback(async () => {
    setRealTimeAdaptive(p => {
      if (p.questionsGenerated >= p.totalQuestions) { submitAssessment(); return p; }
      return p;
    });
    setNextQuestionLoading(true);
    try {
      const res = await fetch('/api/generate-next-adaptive-question', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const data = await res.json();
      if (data.success) {
        const q = {
          ...data.question,
          question_index: realTimeAdaptive.questionsGenerated,
          subconcept: data.question.subconcept || 'General',
          difficulty: data.current_difficulty || 'medium',
          generated_at: new Date().toISOString(),
          was_validated: data.validation_status === 'validated',
        };
        if (q.options && typeof q.options === 'object' && !Array.isArray(q.options)) {
          q.options = ['A', 'B', 'C', 'D'].map(l => q.options[l] || q.options[l.toLowerCase()] || `Option ${l}`);
        }
        setQuestions(p => [...p, q]);
        setCurrentQuestionIndex(realTimeAdaptive.questionsGenerated);

        if (realTimeAdaptive.currentDifficulty !== data.current_difficulty) {
          setShowDifficultyChange({ oldDiff: realTimeAdaptive.currentDifficulty, newDiff: data.current_difficulty });
        }

        setRealTimeAdaptive(p => ({ ...p, questionsGenerated: p.questionsGenerated + 1, currentDifficulty: data.current_difficulty || p.currentDifficulty }));

        if (!assessmentStarted) {
          setAssessmentStarted(true);
          startTimeRef.current = new Date();
          timerRef.current = setInterval(() => {
            const s = Math.floor((new Date() - startTimeRef.current) / 1000);
            setTimer(`${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`);
          }, 1000);
        }
        setLoading(false);
        setNextQuestionLoading(false);
        qStartTimeRef.current = new Date();
      } else if (data.message === 'Assessment complete') {
        await submitAssessment();
      } else throw new Error(data.message || 'Question generation failed');
    } catch (err) {
      if (realTimeAdaptive.questionsGenerated < realTimeAdaptive.totalQuestions) setTimeout(generateNextQuestion, 2000);
      else { setError(err.message); setLoading(false); setNextQuestionLoading(false); }
    }
  }, [realTimeAdaptive, assessmentStarted]);

  const handleAnswerSubmission = useCallback(async (isCorrect, qIdx) => {
    const q = questions[qIdx];
    setRealTimeAdaptive(p => ({
      ...p, performance: {
        correct: p.performance.correct + (isCorrect ? 1 : 0),
        total: p.performance.total + 1,
        consecutiveCorrect: isCorrect ? p.performance.consecutiveCorrect + 1 : 0,
        consecutiveWrong: isCorrect ? 0 : p.performance.consecutiveWrong + 1,
      },
    }));
    try {
      const res = await fetch('/api/update-adaptive-performance', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_correct: isCorrect, difficulty: q.difficulty, subconcept: q.subconcept || 'General' }),
      });
      const data = await res.json();
      if (data.success) setRealTimeAdaptive(p => ({ ...p, currentDifficulty: data.new_difficulty }));
    } catch (e) {}
    await generateNextQuestion();
  }, [questions, realTimeAdaptive, generateNextQuestion]);

  const checkFinalAchievements = useCallback(() => {
    const correct = accurateScoreTracker.correctCount;
    const total = accurateScoreTracker.totalQuestions || questions.length;
    const acc = (correct / total) * 100;
    const totalTime = startTimeRef.current ? (new Date() - startTimeRef.current) / 1000 : 0;
    if (totalTime / questions.length < 30) awardBadge('QUICK_THINKER');
    if (acc >= 90) awardBadge('PERFECT_SCORE');
    else if (acc >= 80) awardBadge('HIGH_SCORER');
    else if (acc >= 70) awardBadge('SOLID_PERFORMER');
    if (correct === questions.length) awardBadge('FLAWLESS_VICTORY');
    if (Object.keys(accurateScoreTracker.userAnswers).length === questions.length) awardBadge('DETERMINED_LEARNER');
  }, [accurateScoreTracker, questions, awardBadge]);

  const submitAssessment = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    checkFinalAchievements();
    if (isPrimaryGrade) {
      setShowCoinRain(true);
      AudioEngine.submit();
    } else {
      AudioEngine.proSubmit();
    }
    setShowSubmitModal(true);
    const correct = accurateScoreTracker.correctCount;
    const total = accurateScoreTracker.totalQuestions || questions.length;
    const payload = {
      performance_data: { correct, total, conceptPerformance: accurateScoreTracker.conceptPerformance,
        questions: questions.map((q, i) => ({ ...q, user_answer: accurateScoreTracker.userAnswers[i]?.selected, is_correct: accurateScoreTracker.userAnswers[i]?.isCorrect || false, time_taken: qTimesRef.current[i] || 0 })),
        badges_earned: earnedBadges },
      time_taken: qTimesRef.current, badges_earned: earnedBadges,
    };
    try {
      const res = await fetch('/api/submit-real-time-assessment', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success) setTimeout(() => { window.location.href = '/dashboard'; }, 4500);
    } catch (err) { setTimeout(() => { window.location.href = '/dashboard'; }, 4500); }
  }, [accurateScoreTracker, questions, earnedBadges, checkFinalAchievements, isPrimaryGrade]);

  const selectOption = useCallback((optionIndex, event) => {
    if (answerFeedback) return;
    isPrimaryGrade ? AudioEngine.click() : AudioEngine.proClick();

    const endTime = new Date();
    const timeSpent = qStartTimeRef.current ? (endTime - qStartTimeRef.current) / 1000 : 0;
    qTimesRef.current[currentQuestionIndex] = timeSpent;
    const q = questions[currentQuestionIndex];
    let correctIndex = -1;
    const ca = q.correct_answer;
    if (typeof ca === 'string' && ['A', 'B', 'C', 'D'].includes(ca.toUpperCase())) correctIndex = ca.toUpperCase().charCodeAt(0) - 65;
    else if (typeof ca === 'number' && ca >= 0 && ca <= 3) correctIndex = ca;
    else if (typeof ca === 'string' && /^[0-3]$/.test(ca)) correctIndex = parseInt(ca);
    const isCorrect = optionIndex === correctIndex;

    setAccurateScoreTracker(p => {
      const n = { ...p };
      n.userAnswers = { ...n.userAnswers, [currentQuestionIndex]: { selected: optionIndex, correct: correctIndex, isCorrect, timeSpent } };
      if (isCorrect) n.correctCount++;
      const ck = `${q.concept || 'Unknown'}_${q.subconcept || 'Unknown'}`;
      if (!n.conceptPerformance[ck]) n.conceptPerformance[ck] = { concept: q.concept, subconcept: q.subconcept, correct: 0, total: 0 };
      n.conceptPerformance[ck].total++;
      if (isCorrect) n.conceptPerformance[ck].correct++;
      return n;
    });

    setUserAnswers(p => ({ ...p, [currentQuestionIndex]: optionIndex }));
    setAnswerFeedback({ isCorrect, selectedIndex: optionIndex, correctIndex });

    if (isCorrect) {
      isPrimaryGrade ? AudioEngine.correct() : AudioEngine.proCorrect();
      const pts = q.difficulty === 'hard' ? 30 : q.difficulty === 'medium' ? 20 : 10;
      setXp(p => p + pts);
      if (isPrimaryGrade) {
        AudioEngine.coin();
        showEncouragement();
        setShowCoinRain(true);
        setTimeout(() => setShowCoinRain(false), 1500);
      } else {
        setEncouragementMessage('correct');
      }
    } else {
      isPrimaryGrade ? AudioEngine.wrong() : AudioEngine.proWrong();
    }

    checkAchievements(currentQuestionIndex, isCorrect, timeSpent, q.difficulty);
    setTimeout(() => {
      setAnswerFeedback(null);
      setEncouragementMessage(null);
      handleAnswerSubmission(isCorrect, currentQuestionIndex);
    }, 1600);
  }, [currentQuestionIndex, questions, answerFeedback, showEncouragement, checkAchievements, handleAnswerSubmission, isPrimaryGrade]);

  useEffect(() => {
    const handleKey = (e) => {
      if (!assessmentStarted || answerFeedback || loading || nextQuestionLoading) return;
      if (['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault();
        const idx = parseInt(e.key) - 1;
        if (idx < (questions[currentQuestionIndex]?.options?.length || 0)) selectOption(idx);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [assessmentStarted, answerFeedback, loading, nextQuestionLoading, questions, currentQuestionIndex, selectOption]);

  useEffect(() => {
    const handler = (e) => {
      if (assessmentStarted && !showSubmitModal) { e.preventDefault(); e.returnValue = 'Assessment in progress!'; return e.returnValue; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [assessmentStarted, showSubmitModal]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetch('/api/assessment', { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
        if (!res.ok) { if (res.status === 401) { window.location.href = '/'; return; } throw new Error(`HTTP ${res.status}`); }
        const data = await res.json();
        if (!data.success) { if (data.redirect) window.location.href = data.redirect; else { setError(data.error || 'Failed'); setLoading(false); } return; }
        setUserData(data.user);
        setTotalQuestions(data.total_questions);
        await startAdaptive();
      } catch (err) { setError(err.message); setLoading(false); }
    };
    init();
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = realTimeAdaptive.questionsGenerated >= realTimeAdaptive.totalQuestions;
  const options = currentQuestion?.options || [];
  const streak = currentAchievements.perfectStreak;

  // ── Full-screen loader ────────────────────────────────────────
  if (loading && questions.length === 0 && !error) {
    if (isPrimaryGrade) {
      return (
        <div className="asm-root">
          <ParticleCanvas />
          <div className="boot-screen">
            <div className="boot-logo">
              <div className="boot-ring r1" /><div className="boot-ring r2" /><div className="boot-ring r3" />
              <span className="boot-icon">🧠</span>
            </div>
            <div className="boot-title">SolveSmart</div>
            <div className="boot-sub">{loadingMessage}</div>
            <div className="boot-track"><div className="boot-fill" style={{ width: `${loadingProgress}%` }} /></div>
            <div className="boot-dots"><span /><span /><span /></div>
            <div className="boot-tagline">Adaptive Learning Engine v2.0</div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="asm-root pro-root">
          <div className="pro-boot-screen">
            <div className="pro-boot-logo">
              <div className="pro-boot-spinner" />
              <span className="pro-boot-icon">◎</span>
            </div>
            <div className="pro-boot-title">SolveSmart</div>
            <div className="pro-boot-sub">{loadingMessage}</div>
            <div className="pro-boot-track"><div className="pro-boot-fill" style={{ width: `${loadingProgress}%` }} /></div>
            <div className="pro-boot-tagline">Adaptive Assessment System</div>
          </div>
        </div>
      );
    }
  }

  if (error) {
    return (
      <div className={`asm-root ${!isPrimaryGrade ? 'pro-root' : ''}`}>
        {isPrimaryGrade && <ParticleCanvas />}
        <div className={isPrimaryGrade ? 'error-screen' : 'pro-error-screen'}>
          <div className={isPrimaryGrade ? 'error-icon' : 'pro-error-icon'}>{isPrimaryGrade ? '⚠️' : '!'}</div>
          <h3>{isPrimaryGrade ? 'Connection Failed' : 'Connection Error'}</h3>
          <p>{error}</p>
          <button className={isPrimaryGrade ? 'btn-primary' : 'pro-btn'} onClick={() => window.location.reload()}>
            {isPrimaryGrade ? '🔄 Try Again' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // ── PROFESSIONAL UI (Grades 6–12) ────────────────────────────
  // ════════════════════════════════════════════════════════════════
  if (!isPrimaryGrade) {
    return (
      <div className="asm-root pro-root">

        {/* Pro onboarding */}
        {showGuide && (
          <ProOnboarding onComplete={() => {
            setShowGuide(false);
            localStorage.setItem('solvesmart_visited', 'true');
          }} />
        )}

        {/* Difficulty change */}
        {showDifficultyChange && (
          <ProDifficultyChangeNotif
            oldDiff={showDifficultyChange.oldDiff}
            newDiff={showDifficultyChange.newDiff}
            onClose={() => setShowDifficultyChange(null)}
          />
        )}

        {/* Encouragement (subtle correct flash for pro) */}
        {encouragementMessage === 'correct' && (
          <ProCorrectFlash onClose={() => setEncouragementMessage(null)} />
        )}

        {/* Toast stack — pro style */}
        <div className="pro-toast-stack">
          {toastQueue.map(t => (
            <ProNotificationToast key={t.id} badge={t.badge} onDone={() => setToastQueue(p => p.filter(x => x.id !== t.id))} />
          ))}
        </div>

        {/* Submit modal */}
        {showSubmitModal && (
          <ProSubmitModal
            correct={accurateScoreTracker.correctCount}
            total={accurateScoreTracker.totalQuestions || questions.length}
            xp={xp}
            badges={earnedBadges.length}
            accuracy={accuracy}
          />
        )}

        {/* Distribution modal */}
        {showDistributionModal && (
          <ProDistributionModal data={distributionData} onClose={() => setShowDistributionModal(false)} />
        )}

        <div className="pro-layout">

          {/* ── Pro Top Bar ───────────────────────────────────── */}
          <header className="pro-top-bar">
            <div className="pro-top-left">
              <div className="pro-brand">
                <span className="pro-brand-mark">◈</span>
                <span className="pro-brand-name">SolveSmart</span>
              </div>
              <div className="pro-grade-tag">Grade {userData?.grade || '9'} · CBSE</div>
            </div>

            <ProProgressBar current={realTimeAdaptive.questionsGenerated} total={realTimeAdaptive.totalQuestions} score={xp} />

            <div className="pro-top-right">
              <ProAccuracyDisplay correct={accurateScoreTracker.correctCount} total={realTimeAdaptive.performance.total || realTimeAdaptive.questionsGenerated} />
              {streak >= 2 && (
                <div className="pro-streak-pill">
                  <span>{streak}</span>
                  <span>streak</span>
                </div>
              )}
              <div className="pro-timer-pill">
                <span className="pro-timer-val">{timer}</span>
              </div>
              <div className={`pro-diff-chip pro-diff-${realTimeAdaptive.currentDifficulty}`}>
                {realTimeAdaptive.currentDifficulty}
              </div>
              <button className="pro-icon-btn" onClick={() => setShowDistributionModal(true)} title="View distribution">
                ≡
              </button>
            </div>
          </header>

          {/* ── Pro Progress Dots ─────────────────────────────── */}
          <div className="pro-nav-track">
            {Array.from({ length: realTimeAdaptive.totalQuestions }).map((_, i) => {
              const ans = accurateScoreTracker.userAnswers[i];
              const state = i === currentQuestionIndex ? 'current' : ans ? (ans.isCorrect ? 'correct' : 'wrong') : i < realTimeAdaptive.questionsGenerated ? 'done' : 'pending';
              return <div key={i} className={`pro-nav-dot pro-nav-${state}`} title={`Q${i + 1}`} />;
            })}
          </div>

          {/* ── Pro Question Area ─────────────────────────────── */}
          {currentQuestion && (
            <main className="pro-question-area" key={currentQuestionIndex}>

              <div className={`pro-q-card ${nextQuestionLoading ? 'pro-q-loading' : ''}`}>

                {nextQuestionLoading && (
                  <div className="pro-q-overlay">
                    <div className="pro-q-spinner" />
                    <span>Loading next question</span>
                  </div>
                )}

                {/* Meta */}
                <div className="pro-q-meta">
                  <div className="pro-q-num">
                    <span>{currentQuestionIndex + 1}</span>
                    <span className="pro-q-total">/ {realTimeAdaptive.totalQuestions}</span>
                  </div>
                  <div className="pro-q-tags">
                    <span className="pro-q-concept">{currentQuestion.concept || 'Mathematics'}</span>
                    {currentQuestion.subconcept && (
                      <><span className="pro-q-sep">›</span><span className="pro-q-sub">{currentQuestion.subconcept.replace(/_/g, ' ')}</span></>
                    )}
                  </div>
                  <div className={`pro-q-diff pro-diff-badge-${currentQuestion.difficulty}`}>
                    {currentQuestion.difficulty}
                    {currentQuestion.was_validated && <span className="pro-val-dot" />}
                  </div>
                </div>

                {/* Question text */}
                <div className="pro-q-text">{currentQuestion.question}</div>

                {/* Options */}
                <div className="pro-options-grid">
                  {options.map((opt, idx) => {
                    let state = 'idle';
                    if (answerFeedback) {
                      if (idx === answerFeedback.correctIndex) state = 'correct';
                      else if (idx === answerFeedback.selectedIndex && !answerFeedback.isCorrect) state = 'wrong';
                      else state = 'dim';
                    }
                    return (
                      <button
                        key={idx}
                        className={`pro-opt-btn pro-opt-${state}`}
                        onClick={() => !answerFeedback && selectOption(idx)}
                        disabled={!!answerFeedback || nextQuestionLoading}
                      >
                        <span className="pro-opt-letter">{String.fromCharCode(65 + idx)}</span>
                        <span className="pro-opt-text">{opt || 'Option not available'}</span>
                        {state === 'correct' && <span className="pro-opt-check">✓</span>}
                        {state === 'wrong' && <span className="pro-opt-cross">✗</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Feedback */}
                {answerFeedback && (
                  <div className={`pro-feedback pro-fb-${answerFeedback.isCorrect ? 'correct' : 'wrong'}`}>
                    <span>{answerFeedback.isCorrect ? 'Correct' : `Incorrect — Answer: ${String.fromCharCode(65 + answerFeedback.correctIndex)}`}</span>
                    {answerFeedback.isCorrect && (
                      <span className="pro-fb-pts">+{currentQuestion.difficulty === 'hard' ? 30 : currentQuestion.difficulty === 'medium' ? 20 : 10} pts</span>
                    )}
                  </div>
                )}
              </div>

              {/* Stats row */}
              <div className="pro-stats-row">
                {[
                  { val: accurateScoreTracker.correctCount, label: 'Correct' },
                  { val: realTimeAdaptive.performance.total, label: 'Answered' },
                  { val: `${accuracy}%`, label: 'Accuracy' },
                  { val: streak > 0 ? streak : '—', label: 'Streak' },
                ].map((s, i) => (
                  <div key={i} className="pro-stat-card">
                    <span className="pro-stat-val">{s.val}</span>
                    <span className="pro-stat-label">{s.label}</span>
                  </div>
                ))}
              </div>

              {/* Milestones row */}
              {earnedBadges.length > 0 && (
                <div className="pro-milestones-row">
                  <span className="pro-milestones-label">Milestones</span>
                  <div className="pro-milestones-chips">
                    {earnedBadges.map(b => {
                      const cfg = BADGE_CONFIG[b]; if (!cfg) return null;
                      return (
                        <span key={b} className="pro-milestone-chip" title={`${cfg.title}: ${cfg.description}`}>
                          <span>{cfg.icon}</span>
                          <span>{cfg.title}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Submit */}
              {isLastQuestion && !answerFeedback && (
                <button className="pro-submit-btn" onClick={submitAssessment}>
                  Submit Assessment
                  <span className="pro-submit-arrow">→</span>
                </button>
              )}
            </main>
          )}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // ── GAMIFIED UI (Grades 1–5) — Original ─────────────────────
  // ════════════════════════════════════════════════════════════════
  return (
    <div className={`asm-root ${isPrimaryGrade ? 'grade-primary' : ''}`}>
      <ParticleCanvas />
      <CoinRain active={showCoinRain} count={12} />

      <div className="xp-popups">
        {xpPopups.map(p => <div key={p.id} className="xp-popup">+{p.amount} XP</div>)}
      </div>

      {showGuide && (
        <GameTour isPrimary={isPrimaryGrade} onComplete={() => {
          setShowGuide(false);
          localStorage.setItem('solvesmart_visited', 'true');
          AudioEngine.levelUp();
        }} />
      )}

      {showDifficultyChange && (
        <DifficultyChangeNotif
          oldDiff={showDifficultyChange.oldDiff}
          newDiff={showDifficultyChange.newDiff}
          onClose={() => setShowDifficultyChange(null)}
        />
      )}

      {encouragementMessage && encouragementMessage !== 'correct' && (
        <EncouragementMessage message={encouragementMessage} onClose={() => setEncouragementMessage(null)} />
      )}

      <div className="toast-stack">
        {toastQueue.map(t => (
          <AchievementToast key={t.id} badge={t.badge} onDone={() => setToastQueue(p => p.filter(x => x.id !== t.id))} />
        ))}
      </div>

      {showSubmitModal && (
        <SubmitCelebration
          correct={accurateScoreTracker.correctCount}
          total={accurateScoreTracker.totalQuestions || questions.length}
          xp={xp}
          badges={earnedBadges.length}
          accuracy={accuracy}
        />
      )}

      {showDistributionModal && (
        <DistributionModal data={distributionData} onClose={() => setShowDistributionModal(false)} />
      )}

      <div className="asm-layout">
        <header className="top-bar">
          <div className="top-bar-left">
            <div className="brand-mark">
              <span className="brand-icon">◈</span>
              <span className="brand-name">SolveSmart</span>
            </div>
            <div className="grade-chip">Grade {userData?.grade || '6'} · CBSE</div>
          </div>

          <XPBar current={realTimeAdaptive.questionsGenerated} total={realTimeAdaptive.totalQuestions} score={xp} />

          <div className="top-bar-right">
            <AccuracyDisplay correct={accurateScoreTracker.correctCount} total={realTimeAdaptive.performance.total || realTimeAdaptive.questionsGenerated} />
            <StreakBadge streak={streak} />
            <div className="stat-pill">
              <span>⏱</span>
              <span className="mono">{timer}</span>
            </div>
            <div className={`diff-pill ${getDifficultyColor(realTimeAdaptive.currentDifficulty)}`}>
              <span>{getDifficultyIcon(realTimeAdaptive.currentDifficulty)}</span>
              <span>{realTimeAdaptive.currentDifficulty}</span>
            </div>
            <button className="icon-btn" onClick={() => { AudioEngine.click(); setShowDistributionModal(true); }}>📊</button>
          </div>
        </header>

        <div className="nav-dots-bar">
          {Array.from({ length: realTimeAdaptive.totalQuestions }).map((_, i) => {
            const ans = accurateScoreTracker.userAnswers[i];
            const state = i === currentQuestionIndex ? 'current' : ans ? (ans.isCorrect ? 'correct' : 'wrong') : i < realTimeAdaptive.questionsGenerated ? 'done' : 'pending';
            return <div key={i} className={`ndot ndot-${state}`} title={`Q${i + 1}`} />;
          })}
        </div>

        {currentQuestion && (
          <main className="question-area" key={currentQuestionIndex}>
            <div className={`q-card ${nextQuestionLoading ? 'q-card-loading' : ''}`}>
              <div className={`q-card-glow ${getDifficultyColor(currentQuestion.difficulty)}`} />

              {nextQuestionLoading && (
                <div className="q-next-overlay">
                  <div className="q-next-spinner" />
                  <span>Loading next question...</span>
                </div>
              )}

              <div className="q-meta">
                <div className="q-num-badge">{currentQuestionIndex + 1}</div>
                <div className="q-tags">
                  <span className="q-concept">{currentQuestion.concept || 'Mathematics'}</span>
                  {currentQuestion.subconcept && <><span className="q-sep">›</span><span className="q-subconcept">{currentQuestion.subconcept.replace(/_/g, ' ')}</span></>}
                </div>
                <div className={`q-diff-badge ${getDifficultyColor(currentQuestion.difficulty)}`}>
                  <span>{getDifficultyIcon(currentQuestion.difficulty)}</span>
                  <span>{currentQuestion.difficulty}</span>
                  {currentQuestion.was_validated && <span className="val-dot" />}
                </div>
              </div>

              <div className="q-text">{currentQuestion.question}</div>

              <div className="options-grid">
                {options.map((opt, idx) => {
                  let state = 'idle';
                  if (answerFeedback) {
                    if (idx === answerFeedback.correctIndex) state = 'correct';
                    else if (idx === answerFeedback.selectedIndex && !answerFeedback.isCorrect) state = 'wrong';
                    else state = 'dim';
                  }
                  return (
                    <button
                      key={idx}
                      className={`opt-btn opt-${state}`}
                      onClick={() => !answerFeedback && selectOption(idx)}
                      onMouseEnter={() => !answerFeedback && AudioEngine.hover()}
                      disabled={!!answerFeedback || nextQuestionLoading}
                    >
                      <span className="opt-letter">{String.fromCharCode(65 + idx)}</span>
                      <span className="opt-text">{opt || 'Option not available'}</span>
                      {state === 'correct' && <span className="opt-result-icon correct-icon">✓</span>}
                      {state === 'wrong' && <span className="opt-result-icon wrong-icon">✗</span>}
                    </button>
                  );
                })}
              </div>

              {answerFeedback && (
                <div className={`feedback-banner fb-${answerFeedback.isCorrect ? 'correct' : 'wrong'}`}>
                  <span className="fb-icon">{answerFeedback.isCorrect ? '✦ Correct!' : '✗ Not quite'}</span>
                  {answerFeedback.isCorrect && <span className="fb-xp">+{currentQuestion.difficulty === 'hard' ? 30 : currentQuestion.difficulty === 'medium' ? 20 : 10} XP 🪙</span>}
                  {!answerFeedback.isCorrect && <span className="fb-hint">Answer: {String.fromCharCode(65 + answerFeedback.correctIndex)}</span>}
                </div>
              )}
            </div>

            <div className="stats-row">
              {[
                { val: accurateScoreTracker.correctCount, label: 'Correct', icon: '✅' },
                { val: realTimeAdaptive.performance.total, label: 'Answered', icon: '📋' },
                { val: `${accuracy}%`, label: 'Accuracy', icon: '🎯', col: accuracy >= 80 ? '#34d399' : accuracy >= 60 ? '#fbbf24' : '#f87171' },
                { val: streak > 0 ? `${streak}🔥` : '—', label: 'Streak', icon: '🔥' },
              ].map((s, i) => (
                <div key={i} className="stat-card">
                  <span className="sc-icon">{s.icon}</span>
                  <span className="sc-val" style={s.col ? { color: s.col } : {}}>{s.val}</span>
                  <span className="sc-label">{s.label}</span>
                </div>
              ))}
            </div>

            {earnedBadges.length > 0 && (
              <div className="badges-row">
                {earnedBadges.map(b => {
                  const cfg = BADGE_CONFIG[b]; if (!cfg) return null;
                  return (
                    <span key={b} className="badge-chip" title={cfg.title} style={{ '--bc': cfg.color }}>
                      {cfg.icon}
                      <span className="badge-tooltip">{cfg.title}</span>
                    </span>
                  );
                })}
              </div>
            )}

            {isLastQuestion && !answerFeedback && (
              <button className="submit-btn" onClick={submitAssessment} onMouseEnter={() => AudioEngine.hover()}>
                <span>🏁 Submit Assessment</span>
                <span className="submit-arrow">→</span>
              </button>
            )}
          </main>
        )}
      </div>
    </div>
  );
};

export default Assessment;